/**
 * Pannello coach del diario di salute, dentro un Dialog aperto dalla lista
 * atleti. Due schede: "Configura" (abilita + scelta campi per questo atleta) e
 * "Andamenti" (grafici per campo). I dati sono dell'atleta: il coach li
 * consulta e decide cosa chiedere, non li compila.
 * Spec: docs/wellness/00-analisi-e-piano.md
 */
import { useState, useEffect, useCallback } from "react";
import { fetchAPI } from "../../lib/utils";
import { WellnessCharts } from "./WellnessCharts";
import { WellnessFieldPicker } from "./WellnessFieldPicker";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
import { Loader2, HeartPulse } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../../contexts/LanguageContext";

export function WellnessCoachDialog({ athlete, open, onOpenChange }) {
	const { language, t } = useLanguage();
	const athleteId = athlete?.user_id;
	const [tab, setTab] = useState("config");
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [catalog, setCatalog] = useState([]);
	const [enabled, setEnabled] = useState(false);
	const [reminderEnabled, setReminderEnabled] = useState(true);
	const [selectedFields, setSelectedFields] = useState([]);
	const [consentInfo, setConsentInfo] = useState(null);
	const [entries, setEntries] = useState([]);

	const load = useCallback(async () => {
		if (!athleteId) return;
		setLoading(true);
		try {
			const [catalogData, configData, entriesData] = await Promise.all([
				fetchAPI(`/wellness/catalog?language=${language}`).catch(() => null),
				fetchAPI(`/wellness/athletes/${athleteId}/config`).catch(() => null),
				fetchAPI(`/wellness/athletes/${athleteId}/entries`).catch(() => null),
			]);
			setCatalog(catalogData?.fields || []);
			setEnabled(!!configData?.enabled);
			setReminderEnabled(configData?.reminder_enabled !== false);
			setSelectedFields(
				configData?.fields?.length
					? configData.fields
					: catalogData?.default_fields || [],
			);
			setConsentInfo({
				consent_required: configData?.consent_required,
				consent_accepted_at: configData?.consent_accepted_at,
			});
			setEntries(entriesData?.entries || []);
		} catch {
			toast.error(t("wellnessLoadError"));
		} finally {
			setLoading(false);
		}
	}, [athleteId, language, t]);

	useEffect(() => {
		if (open) {
			setTab("config");
			load();
		}
	}, [open, load]);

	const toggleField = (key) => {
		setSelectedFields((prev) =>
			prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
		);
	};

	const handleSave = async () => {
		setSaving(true);
		try {
			const result = await fetchAPI(`/wellness/athletes/${athleteId}/config`, {
				method: "PUT",
				body: JSON.stringify({
					enabled,
					fields: selectedFields,
					reminder_enabled: reminderEnabled,
				}),
			});
			setEnabled(!!result?.enabled);
			setReminderEnabled(result?.reminder_enabled !== false);
			setSelectedFields(result?.fields || []);
			setConsentInfo({
				consent_required: result?.consent_required,
				consent_accepted_at: result?.consent_accepted_at,
			});
			toast.success(t("wellnessConfigurationSaved"));
		} catch {
			toast.error(t("wellnessSaveError"));
		} finally {
			setSaving(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="bg-card border-border w-[calc(100vw-2rem)] max-w-2xl max-h-[85vh] overflow-hidden">
				<DialogDescription className="hidden" />
				<DialogHeader>
					<DialogTitle className="font-heading flex items-center gap-2 pr-6">
						<HeartPulse className="w-4 h-4 text-volt" />
						{t("wellnessDiary")} · {athlete?.name}
					</DialogTitle>
				</DialogHeader>

				<div className="flex gap-1.5 border-b border-border pb-2">
					<Button
						variant={tab === "config" ? "default" : "secondary"}
						size="sm"
						className={`rounded-sm ${tab === "config" ? "bg-volt hover:bg-volt/90" : ""}`}
						onClick={() => setTab("config")}
					>
						{t("wellnessConfigure")}
					</Button>
					<Button
						variant={tab === "charts" ? "default" : "secondary"}
						size="sm"
						className={`rounded-sm ${tab === "charts" ? "bg-volt hover:bg-volt/90" : ""}`}
						onClick={() => setTab("charts")}
					>
						{t("wellnessTrends")}
					</Button>
				</div>

				<div className="overflow-y-auto max-h-[60vh] pr-1">
					{loading ? (
						<div className="flex items-center justify-center py-10 text-muted-foreground">
							<Loader2 className="w-5 h-5 animate-spin mr-2" />
							{t("loading")}
						</div>
					) : tab === "config" ? (
						<div className="space-y-4 py-1">
							<div className="flex items-center justify-between gap-3 p-3 rounded-sm bg-secondary border border-border">
								<div className="min-w-0">
									<p className="text-sm font-medium">{t("wellnessEnabled")}</p>
									<p className="text-xs text-muted-foreground">
										{t("wellnessEnabledDescription")}
									</p>
								</div>
								<Switch checked={enabled} onCheckedChange={setEnabled} />
							</div>

							{enabled && (
								<div className="flex items-center justify-between gap-3 p-3 rounded-sm bg-secondary border border-border">
									<div className="min-w-0">
										<p className="text-sm font-medium">
											{t("wellnessDailyReminder")}
										</p>
										<p className="text-xs text-muted-foreground">
											{t("wellnessDailyReminderDescription")}
										</p>
									</div>
									<Switch
										checked={reminderEnabled}
										onCheckedChange={setReminderEnabled}
									/>
								</div>
							)}

							{enabled && consentInfo && (
								<div className="text-xs">
									{consentInfo.consent_required ? (
										<Badge
											variant="outline"
											className="rounded-sm text-amber-400 border-amber-500/40"
										>
											{t("wellnessConsentPending")}
										</Badge>
									) : (
										<Badge className="rounded-sm bg-green-500/20 text-green-400 border-0">
											{t("wellnessConsentAccepted")}
										</Badge>
									)}
								</div>
							)}

							<WellnessFieldPicker
								catalog={catalog}
								selected={selectedFields}
								onToggle={toggleField}
							/>

							<Button
								className="w-full bg-volt hover:bg-volt/90 rounded-sm"
								disabled={saving}
								onClick={handleSave}
							>
								{saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
								{t("wellnessSaveConfiguration")}
							</Button>
						</div>
					) : (
						<div className="space-y-4 py-1">
							{!enabled ? (
								<p className="text-sm text-muted-foreground py-6 text-center">
									{t("wellnessEnableToCollect")}
								</p>
							) : (
								<WellnessCharts
									entries={entries}
									catalog={catalog}
									fields={selectedFields}
									emptyLabel={t("wellnessNoAthleteData")}
								/>
							)}
						</div>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
}
